import { useEffect, useState } from 'react';
import { calendarSettingsInputSchema, calendarSettingsRowSchema, type CalendarSettingsRow } from '@easyflow/shared';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { usePermissions } from '@/contexts/PermissionContext';
import { apiClient } from '@/lib/api';

/** Existing tenant settings, shared by every public form; not per-form rules. */
export default function CalendarBookingWindowSettings() {
  const { hasPermission } = usePermissions();
  const canManage = hasPermission('calendar.manage');
  const [minimum, setMinimum] = useState('');
  const [maximum, setMaximum] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const applySettings = (settings: CalendarSettingsRow | null) => {
    setMinimum(settings?.min_advance_booking == null ? '' : String(settings.min_advance_booking));
    setMaximum(settings?.max_advance_booking == null ? '' : String(settings.max_advance_booking));
  };
  useEffect(() => {
    let active = true;
    void apiClient.get<unknown>('/calendar/settings').then(response => {
      if (!response.success) throw new Error('Impossibile caricare le impostazioni');
      const settings = calendarSettingsRowSchema.nullable().parse(response.data);
      if (active) { applySettings(settings); setLoaded(true); }
    }).catch(() => {
      if (active) setError('Impossibile caricare i limiti di prenotazione. Ricarica la pagina.');
    });
    return () => { active = false; };
  }, []);
  const save = async () => {
    if (!canManage || !loaded || saving) return;
    setError(null);
    setSaved(false);
    const parsed = calendarSettingsInputSchema.safeParse({
      min_advance_booking: minimum === '' ? null : Number(minimum),
      max_advance_booking: maximum === '' ? null : Number(maximum),
    });
    if (!parsed.success) {
      setError('Inserisci numeri interi maggiori o uguali a zero, oppure lascia vuoto per usare il valore predefinito.');
      return;
    }
    setSaving(true);
    try {
      const response = await apiClient.put<unknown>('/calendar/settings', parsed.data);
      if (!response.success) throw new Error('Salvataggio non riuscito');
      calendarSettingsRowSchema.parse(response.data);
      // Re-read the canonical settings, not a local optimistic copy.
      const reread = await apiClient.get<unknown>('/calendar/settings');
      if (!reread.success) throw new Error('Rilettura non riuscita');
      applySettings(calendarSettingsRowSchema.parse(reread.data));
      setSaved(true);
    } catch {
      setError('Impossibile confermare il salvataggio dei limiti di prenotazione. Ricarica prima di riprovare.');
    } finally {
      setSaving(false);
    }
  };
  return <div className="space-y-3">
    <p className="text-sm text-muted-foreground">
      Questi limiti valgono per tutti i form pubblici e per la disponibilità del calendario.
      Il minimo è espresso in minuti, il massimo in giorni. Zero è un valore valido.
    </p>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {!loaded && !error && <p role="status" className="text-sm text-muted-foreground">Caricamento dei limiti…</p>}
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor="calendar-minimum-advance">Anticipo minimo (minuti)</Label>
        <Input id="calendar-minimum-advance" type="number" min={0} step={1} value={minimum} placeholder="0"
          disabled={!loaded || !canManage || saving}
          onChange={event => { setMinimum(event.target.value); setSaved(false); }} />
        <p className="text-xs text-muted-foreground">Vuoto: nessun anticipo minimo (0 minuti).</p>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="calendar-maximum-advance">Anticipo massimo (giorni)</Label>
        <Input id="calendar-maximum-advance" type="number" min={0} step={1} value={maximum} placeholder="90"
          disabled={!loaded || !canManage || saving}
          onChange={event => { setMaximum(event.target.value); setSaved(false); }} />
        <p className="text-xs text-muted-foreground">Vuoto: 90 giorni. Zero: solo la data odierna.</p>
      </div>
    </div>
    {canManage
      ? <Button onClick={save} disabled={!loaded || saving}>{saving ? 'Salvataggio…' : 'Salva limiti di prenotazione'}</Button>
      : <p className="text-sm text-muted-foreground">Sola lettura: serve il permesso di gestione Calendario.</p>}
    {saved && <p role="status" className="text-sm text-muted-foreground">Limiti salvati e riletti dal calendario.</p>}
  </div>;
}
